import { WIDGET_CONTRIBUTION_TYPE } from '@lumapps/widget-contribution/constants';
import { WIDGET_HELP_TYPE } from '@lumapps/widget-help/constants';
import { WIDGET_HTML_TYPE } from '@lumapps/widget-html/constants';
import { htmlValuesToSlate } from '@lumapps/wrex/serialization/slate/utils/htmlValuesToSlate';

interface ParsedWidgetTranslation {
    widgetId: string;
    widgetType: string;
    /** Header text content */
    headerText?: string;
    /** Body value: Wrex.Nodes for contribution widgets, innerHTML for html/help, textContent otherwise */
    bodyValue?: unknown;
    /** Footer text content */
    footerText?: string;
}

interface ParseTranslatedWidgetChunksOptions {
    colorPalette?: any;
    onWidget: (targetLang: string, widget: ParsedWidgetTranslation) => void;
}

/**
 * Parse translated values returned by the auto-translation API
 * and call `onWidget` for each widget found in each target language.
 *
 * Each chunk is expected to contain `<article>` elements with
 * `data-widget-id` and `data-widget-type` attributes, wrapping
 * `<header>`, `<main>`, and `<footer>` sections.
 */
export const parseTranslatedWidgets = (
    translatedValues: Record<string, (string | undefined)[]>,
    { colorPalette, onWidget }: ParseTranslatedWidgetChunksOptions,
): void => {
    const parser = new DOMParser();

    for (const [targetLang, chunks] of Object.entries(translatedValues)) {
        const validChunks = chunks.filter((c): c is string => !!c);

        if (validChunks.length > 0) {
            const doc = parser.parseFromString(validChunks.join(''), 'text/html');
            const articles = doc.querySelectorAll('article');

            articles.forEach((article) => {
                const { widgetId, widgetType } = article.dataset;

                if (!widgetId || !widgetType) {
                    return;
                }

                const header = article.querySelector('header');
                const main = article.querySelector('main');
                const footer = article.querySelector('footer');

                const result: ParsedWidgetTranslation = { widgetId, widgetType };

                if (header?.textContent) {
                    result.headerText = header.textContent;
                }

                if (main?.textContent || main?.innerHTML) {
                    if (widgetType === WIDGET_CONTRIBUTION_TYPE) {
                        result.bodyValue = htmlValuesToSlate({
                            slateHtml: main.innerHTML,
                            pasteOptions: { colorPalette },
                        });
                    } else if (widgetType === WIDGET_HTML_TYPE || widgetType === WIDGET_HELP_TYPE) {
                        result.bodyValue = main.innerHTML;
                    } else {
                        result.bodyValue = main.textContent;
                    }
                }

                if (footer?.textContent) {
                    result.footerText = footer.textContent;
                }

                onWidget(targetLang, result);
            });
        }
    }
};
